import { z } from "zod";
import type { ErrorCode } from "@hybrid-escrow/shared";
import {
  authPrincipalSchema,
  checkoutIntentResponseSchema,
  completeUploadRequestSchema,
  createCheckoutIntentRequestSchema,
  createDisputeRequestSchema,
  createListingRequestSchema,
  disputeResponseSchema,
  downloadResponseSchema,
  listingResponseSchema,
  orderResponseSchema,
  paginatedResponseSchema,
  presignUploadResponseSchema,
  rebuttalRequestSchema,
  submitCheckoutIntentRequestSchema,
  updateListingRequestSchema,
  updateProfileRequestSchema,
  profileDetailsResponseSchema,
  updateProfileDetailsRequestSchema,
  notificationPreferencesSchema,
  updateNotificationPreferencesRequestSchema,
  sellerAnalyticsSchema,
  contactRequestSchema,
  contactResponseSchema,
  createTicketRequestSchema,
  createTicketMessageRequestSchema,
  ticketResponseSchema,
  ticketDetailResponseSchema,
  onrampSessionResponseSchema,
  uploadResponseSchema,
  createReviewRequestSchema,
  sellerReviewResponseSchema,
  sellerRatingSummarySchema,
  createMessageRequestSchema,
  conversationResponseSchema,
  messagesListResponseSchema,
  messageResponseSchema,
  type AuthPrincipal,
  type CheckoutIntentResponse,
  type CreateCheckoutIntentRequest,
  type CreateDisputeRequest,
  type CreateListingRequest,
  type DisputeResponse,
  type DownloadResponse,
  type ListingResponse,
  type OrderResponse,
  type PresignUploadResponse,
  type RebuttalRequest,
  type SubmitCheckoutIntentRequest,
  type UpdateListingRequest,
  type UpdateProfileRequest,
  type OnrampSessionResponse,
  type UploadResponse,
  type CreateReviewRequest,
  type SellerReviewResponse,
  type SellerRatingSummary,
  type CreateMessageRequest,
  type ConversationResponse,
  type MessageResponse,
  type ProfileDetailsResponse,
  type NotificationPreferences,
  type UpdateNotificationPreferencesRequest,
  type UpdateProfileDetailsRequest,
  type SellerAnalytics,
  type ContactRequest,
  type CreateTicketRequest,
  type CreateTicketMessageRequest,
  type TicketDetailResponse,
  type ListListingsQuery,
  watchlistEntryResponseSchema,
  createSavedSearchRequestSchema,
  savedSearchResponseSchema,
  type WatchlistEntryResponse,
  type CreateSavedSearchRequest,
  type SavedSearchResponse,
  createOfferRequestSchema,
  respondToOfferRequestSchema,
  offerResponseSchema,
  type CreateOfferRequest,
  type RespondToOfferRequest,
  type OfferResponse,
  addLicenseKeysRequestSchema,
  licenseKeyResponseSchema,
  revealedLicenseKeyResponseSchema,
  type AddLicenseKeysRequest,
  type RevealedLicenseKeyResponse,
  revealedAccountTransferCredentialsResponseSchema,
  type RevealedAccountTransferCredentialsResponse,
  submitAccountTransferSellerHandoffRequestSchema,
  type SubmitAccountTransferSellerHandoffRequest,
  submitAccountTransferBuyerEmailRequestSchema,
  type SubmitAccountTransferBuyerEmailRequest,
  submitDomainTransferCodeRequestSchema,
  type SubmitDomainTransferCodeRequest,
  revealedDomainTransferCodeResponseSchema,
  type RevealedDomainTransferCodeResponse,
  nftTransferCheckResponseSchema,
  type NftTransferCheckResponse,
  notificationResponseSchema,
  notificationsUnreadCountResponseSchema,
  type NotificationsUnreadCountResponse,
  sellerProfileResponseSchema,
  type SellerProfileResponse,
  postResponseSchema,
  postSummaryResponseSchema,
} from "@hybrid-escrow/shared";
import { publicChainConfigSchema, type PublicChainConfig } from "@hybrid-escrow/config";
import { apiBaseUrl } from "./env";
import { getBearerToken } from "./coinbase/accessTokenBridge";
import { getTurnstileToken } from "./turnstileBridge";

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

const apiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});

/** Typed error thrown by every `apiClient` call for a non-2xx response. */
export class ApiClientError extends Error {
  constructor(
    public readonly code: ErrorCode | "UNKNOWN_ERROR",
    public readonly status: number,
    message: string,
    public readonly requestId?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/** Thrown when the network request itself fails (offline, DNS, CORS, timeout, etc). */
export class ApiNetworkError extends Error {
  constructor(cause: unknown) {
    super("Could not reach the server. Check your connection and try again.");
    this.name = "ApiNetworkError";
    this.cause = cause;
  }
}

// ---------------------------------------------------------------------------
// Fetch core
// ---------------------------------------------------------------------------

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH";
  body?: unknown;
  /** A binary body sent as-is (e.g. a cover image). Mutually exclusive with `body`. */
  rawBody?: Blob;
  /** Attach `Authorization: Bearer <token>`. Defaults to true; set false for public GETs. */
  auth?: boolean;
  /**
   * A stable UUID for this logical mutation attempt, sent as `Idempotency-Key`. Callers
   * must generate this once per user-visible attempt (e.g. once per checkout click) and
   * reuse it across retries of that same attempt — see `newIdempotencyKey()`.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function request<T>(
  path: string,
  // `z.ZodType<T>` defaults its Input type param to T too, which breaks for schemas whose
  // parsed input differs from their output (e.g. address checksum transforms) — widen it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- zod's own Input generic default
  schema: z.ZodType<T, z.ZodTypeDef, any>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, rawBody, auth = true, idempotencyKey, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (rawBody) headers["Content-Type"] = rawBody.type || "application/octet-stream";
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  // Attached regardless of `auth` — consulted by the backend only on the couple of routes that
  // actually check it (new-account creation, the public contact form), and harmless to send
  // everywhere else. Outside `if (auth)` on purpose: the contact form has no CDP session at all
  // but still needs this header.
  const turnstileToken = getTurnstileToken();
  if (turnstileToken) headers["X-Turnstile-Token"] = turnstileToken;
  if (auth) {
    const token = await getBearerToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers,
      body: rawBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal,
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }

  const isJson = response.headers.get("content-type")?.includes("application/json") ?? false;
  const json: unknown = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    const parsedError = apiErrorBodySchema.safeParse(json);
    if (parsedError.success) {
      const { code, message, requestId, details } = parsedError.data.error;
      throw new ApiClientError(code as ErrorCode, response.status, message, requestId, details);
    }
    throw new ApiClientError(
      "UNKNOWN_ERROR",
      response.status,
      `Request failed with status ${response.status}`,
    );
  }

  // Parse (not just cast) every response against the shared zod schema so a drifted
  // backend contract fails loudly in development instead of producing bad UI state.
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new Error(
      `API response for ${method} ${path} did not match the expected schema: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

/** Generates a fresh idempotency/checkout key. One per logical user attempt. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface ListingsQuery {
  cursor?: string;
  limit?: number;
}

export interface DisputesQuery {
  cursor?: string;
  limit?: number;
  status?: DisputeResponse["status"];
}

function toQueryString<T extends object>(params: T): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const apiClient = {
  getPublicConfig: (signal?: AbortSignal) =>
    request<PublicChainConfig>("/api/v1/config/public", publicChainConfigSchema, {
      auth: false,
      signal,
    }),

  getMe: (signal?: AbortSignal) =>
    request<AuthPrincipal>("/api/v1/me", authPrincipalSchema, { signal }),

  updateProfile: (body: UpdateProfileRequest) =>
    request<AuthPrincipal>("/api/v1/me/profile", authPrincipalSchema, {
      method: "PATCH",
      body: updateProfileRequestSchema.parse(body),
    }),

  getNotificationPreferences: (signal?: AbortSignal) =>
    request<NotificationPreferences>(
      "/api/v1/me/notification-preferences",
      notificationPreferencesSchema,
      { signal },
    ),

  updateNotificationPreferences: (body: UpdateNotificationPreferencesRequest) =>
    request<NotificationPreferences>(
      "/api/v1/me/notification-preferences",
      notificationPreferencesSchema,
      { method: "PATCH", body: updateNotificationPreferencesRequestSchema.parse(body) },
    ),

  /** The account's own data as a plain object, for a "Download my data" file. */
  exportAccountData: () => request("/api/v1/me/export", z.record(z.unknown())),

  deleteAccount: () =>
    request("/api/v1/me/delete", z.object({ deleted: z.boolean() }), {
      method: "POST",
      body: { confirm: "DELETE" },
    }),

  getProfileDetails: (signal?: AbortSignal) =>
    request<ProfileDetailsResponse>("/api/v1/me/details", profileDetailsResponseSchema, { signal }),

  updateProfileDetails: (body: UpdateProfileDetailsRequest) =>
    request<ProfileDetailsResponse>("/api/v1/me/details", profileDetailsResponseSchema, {
      method: "PATCH",
      body: updateProfileDetailsRequestSchema.parse(body),
    }),

  /** Requests a ready-to-use, single-use Coinbase Onramp URL for the caller's own wallet — the
   *  destination address always comes from the authenticated session server-side. */
  createOnrampSession: () =>
    request<OnrampSessionResponse>("/api/v1/onramp/session", onrampSessionResponseSchema, {
      method: "POST",
    }),

  // --- Listings -------------------------------------------------------------

  listListings: (query: Partial<ListListingsQuery> = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/listings${toQueryString(query)}`,
      paginatedResponseSchema(listingResponseSchema),
      { auth: false, signal },
    ),

  getListing: (id: string, signal?: AbortSignal) =>
    request<ListingResponse>(`/api/v1/listings/${id}`, listingResponseSchema, {
      auth: false,
      signal,
    }),

  getSellerListings: (signal?: AbortSignal) =>
    request(`/api/v1/seller/listings`, paginatedResponseSchema(listingResponseSchema), { signal }),

  createListing: (body: CreateListingRequest, idempotencyKey: string) =>
    request<ListingResponse>("/api/v1/listings", listingResponseSchema, {
      method: "POST",
      body: createListingRequestSchema.parse(body),
      idempotencyKey,
    }),

  updateListing: (id: string, body: UpdateListingRequest) =>
    request<ListingResponse>(`/api/v1/listings/${id}`, listingResponseSchema, {
      method: "PATCH",
      body: updateListingRequestSchema.parse(body),
    }),

  publishListing: (id: string) =>
    request<ListingResponse>(`/api/v1/listings/${id}/publish`, listingResponseSchema, {
      method: "POST",
    }),

  verifyDomainOwnership: (id: string) =>
    request<ListingResponse>(`/api/v1/listings/${id}/domain/verify`, listingResponseSchema, {
      method: "POST",
    }),

  verifyNftOwnership: (id: string) =>
    request<ListingResponse>(`/api/v1/listings/${id}/nft/verify`, listingResponseSchema, {
      method: "POST",
    }),

  uploadListingCover: (id: string, file: Blob) =>
    request(`/api/v1/listings/${id}/cover`, z.object({ hasCoverImage: z.boolean() }), {
      method: "POST",
      rawBody: file,
    }),

  removeListingCover: (id: string) =>
    request(`/api/v1/listings/${id}/cover/delete`, z.object({ hasCoverImage: z.boolean() }), {
      method: "POST",
    }),

  archiveListing: (id: string) =>
    request<ListingResponse>(`/api/v1/listings/${id}/archive`, listingResponseSchema, {
      method: "POST",
    }),

  deleteListing: (id: string) =>
    request(`/api/v1/listings/${id}/delete`, z.object({ deleted: z.boolean() }), {
      method: "POST",
    }),

  getSellerAnalytics: (signal?: AbortSignal) =>
    request<SellerAnalytics>("/api/v1/seller/analytics", sellerAnalyticsSchema, { signal }),

  // --- License-key fulfillment -------------------------------------------------

  addLicenseKeys: (listingId: string, body: AddLicenseKeysRequest) =>
    request(`/api/v1/listings/${listingId}/license-keys`, z.object({ added: z.number() }), {
      method: "POST",
      body: addLicenseKeysRequestSchema.parse(body),
    }),

  getLicenseKeys: (listingId: string, query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/listings/${listingId}/license-keys${toQueryString(query)}`,
      paginatedResponseSchema(licenseKeyResponseSchema),
      { signal },
    ),

  revealLicenseKey: (orderId: string) =>
    request<RevealedLicenseKeyResponse>(
      `/api/v1/orders/${orderId}/license-key`,
      revealedLicenseKeyResponseSchema,
      { method: "POST" },
    ),

  revealAccountTransferCredentials: (orderId: string) =>
    request<RevealedAccountTransferCredentialsResponse>(
      `/api/v1/orders/${orderId}/account-transfer/reveal`,
      revealedAccountTransferCredentialsResponseSchema,
      { method: "POST" },
    ),

  submitAccountTransferSellerHandoff: (
    orderId: string,
    body: SubmitAccountTransferSellerHandoffRequest,
  ) =>
    request(`/api/v1/orders/${orderId}/account-transfer/seller-handoff`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: submitAccountTransferSellerHandoffRequestSchema.parse(body),
    }),

  submitAccountTransferBuyerEmail: (orderId: string, body: SubmitAccountTransferBuyerEmailRequest) =>
    request(`/api/v1/orders/${orderId}/account-transfer/buyer-email`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: submitAccountTransferBuyerEmailRequestSchema.parse(body),
    }),

  submitDomainTransferCode: (orderId: string, body: SubmitDomainTransferCodeRequest) =>
    request(`/api/v1/orders/${orderId}/domain-transfer/submit`, z.object({ ok: z.boolean() }), {
      method: "POST",
      body: submitDomainTransferCodeRequestSchema.parse(body),
    }),

  revealDomainTransferCode: (orderId: string) =>
    request<RevealedDomainTransferCodeResponse>(
      `/api/v1/orders/${orderId}/domain-transfer/reveal`,
      revealedDomainTransferCodeResponseSchema,
      { method: "POST" },
    ),

  checkNftDelivery: (orderId: string) =>
    request<NftTransferCheckResponse>(
      `/api/v1/orders/${orderId}/nft-transfer/check`,
      nftTransferCheckResponseSchema,
      { method: "POST" },
    ),

  // --- In-app notifications ----------------------------------------------------

  getNotifications: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/me/notifications${toQueryString(query)}`,
      paginatedResponseSchema(notificationResponseSchema),
      { signal },
    ),

  getUnreadNotificationCount: (signal?: AbortSignal) =>
    request<NotificationsUnreadCountResponse>(
      "/api/v1/me/notifications/unread-count",
      notificationsUnreadCountResponseSchema,
      { signal },
    ),

  markNotificationRead: (id: string) =>
    request(`/api/v1/me/notifications/${id}/read`, z.object({ read: z.boolean() }), {
      method: "POST",
    }),

  markAllNotificationsRead: () =>
    request("/api/v1/me/notifications/read-all", z.object({ read: z.boolean() }), {
      method: "POST",
    }),

  // --- Seller storefronts ------------------------------------------------------

  getSellerProfile: (sellerId: string, signal?: AbortSignal) =>
    request<SellerProfileResponse>(`/api/v1/sellers/${sellerId}`, sellerProfileResponseSchema, {
      auth: false,
      signal,
    }),

  // --- Blog -------------------------------------------------------------------

  getPosts: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/posts${toQueryString(query)}`,
      paginatedResponseSchema(postSummaryResponseSchema),
      { auth: false, signal },
    ),

  getPost: (slug: string, signal?: AbortSignal) =>
    request(`/api/v1/posts/${slug}`, postResponseSchema, { auth: false, signal }),

  // --- Watchlist & saved searches --------------------------------------------

  watchListing: (id: string) =>
    request<WatchlistEntryResponse>(`/api/v1/listings/${id}/watch`, watchlistEntryResponseSchema, {
      method: "POST",
    }),

  unwatchListing: (id: string) =>
    request(`/api/v1/listings/${id}/unwatch`, z.object({ watching: z.boolean() }), {
      method: "POST",
    }),

  getWatchlist: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/me/watchlist${toQueryString(query)}`,
      paginatedResponseSchema(watchlistEntryResponseSchema),
      { signal },
    ),

  createSavedSearch: (body: CreateSavedSearchRequest) =>
    request<SavedSearchResponse>("/api/v1/me/saved-searches", savedSearchResponseSchema, {
      method: "POST",
      body: createSavedSearchRequestSchema.parse(body),
    }),

  getSavedSearches: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/me/saved-searches${toQueryString(query)}`,
      paginatedResponseSchema(savedSearchResponseSchema),
      { signal },
    ),

  deleteSavedSearch: (id: string) =>
    request(`/api/v1/me/saved-searches/${id}/delete`, z.object({ deleted: z.boolean() }), {
      method: "POST",
    }),

  // --- Best Offer negotiation -------------------------------------------------

  createOffer: (listingId: string, body: CreateOfferRequest) =>
    request<OfferResponse>(`/api/v1/listings/${listingId}/offers`, offerResponseSchema, {
      method: "POST",
      body: createOfferRequestSchema.parse(body),
    }),

  getOffer: (id: string, signal?: AbortSignal) =>
    request<OfferResponse>(`/api/v1/offers/${id}`, offerResponseSchema, { signal }),

  getMyOffers: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(`/api/v1/me/offers${toQueryString(query)}`, paginatedResponseSchema(offerResponseSchema), {
      signal,
    }),

  getSellerOffers: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/seller/offers${toQueryString(query)}`,
      paginatedResponseSchema(offerResponseSchema),
      { signal },
    ),

  acceptOffer: (id: string) =>
    request<OfferResponse>(`/api/v1/offers/${id}/accept`, offerResponseSchema, { method: "POST" }),

  declineOffer: (id: string) =>
    request<OfferResponse>(`/api/v1/offers/${id}/decline`, offerResponseSchema, { method: "POST" }),

  counterOffer: (id: string, body: RespondToOfferRequest) =>
    request<OfferResponse>(`/api/v1/offers/${id}/counter`, offerResponseSchema, {
      method: "POST",
      body: respondToOfferRequestSchema.parse(body),
    }),

  presignUpload: (
    listingId: string,
    body: { filename: string; declaredMimeType: string; sizeBytes: number },
    idempotencyKey: string,
  ) =>
    request<PresignUploadResponse>(
      `/api/v1/listings/${listingId}/uploads/presign`,
      presignUploadResponseSchema,
      { method: "POST", body, idempotencyKey },
    ),

  completeUpload: (
    listingId: string,
    uploadId: string,
    etag: string | undefined,
    idempotencyKey: string,
  ) =>
    request<UploadResponse>(
      `/api/v1/listings/${listingId}/uploads/${uploadId}/complete`,
      uploadResponseSchema,
      {
        method: "POST",
        body: completeUploadRequestSchema.parse({ etag }),
        idempotencyKey,
      },
    ),

  // --- Checkout / orders ------------------------------------------------------

  createCheckoutIntent: (body: CreateCheckoutIntentRequest) =>
    request<CheckoutIntentResponse>("/api/v1/checkout-intents", checkoutIntentResponseSchema, {
      method: "POST",
      body: createCheckoutIntentRequestSchema.parse(body),
      idempotencyKey: body.idempotencyKey,
    }),

  // No response schema is published for this endpoint (informational-only per the
  // contract), so we accept anything the server returns rather than over-constraining it.
  submitCheckoutIntent: (orderId: string, body: SubmitCheckoutIntentRequest) =>
    request<unknown>(`/api/v1/checkout-intents/${orderId}/submitted`, z.unknown(), {
      method: "POST",
      body: submitCheckoutIntentRequestSchema.parse(body),
    }),

  /** Releases what a checkout reserved (license key, archived listing) after a wallet failure that
   *  never reached the chain. The API refuses once any funding attempt is recorded. */
  cancelCheckoutIntent: (orderId: string) =>
    request<unknown>(`/api/v1/checkout-intents/${orderId}/cancel`, z.unknown(), {
      method: "POST",
    }),

  getOrders: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(`/api/v1/orders${toQueryString(query)}`, paginatedResponseSchema(orderResponseSchema), {
      signal,
    }),

  getOrder: (orderId: string, signal?: AbortSignal) =>
    request<OrderResponse>(`/api/v1/orders/${orderId}`, orderResponseSchema, { signal }),

  reconcileOrder: (orderId: string) =>
    request<OrderResponse>(`/api/v1/orders/${orderId}/reconcile`, orderResponseSchema, {
      method: "POST",
    }),

  downloadOrder: (orderId: string) =>
    request<DownloadResponse>(`/api/v1/orders/${orderId}/download`, downloadResponseSchema, {
      method: "POST",
    }),

  // --- Disputes ---------------------------------------------------------------

  createDispute: (orderId: string, body: CreateDisputeRequest, idempotencyKey: string) =>
    request<DisputeResponse>(`/api/v1/orders/${orderId}/disputes`, disputeResponseSchema, {
      method: "POST",
      body: createDisputeRequestSchema.parse(body),
      idempotencyKey,
    }),

  getDispute: (disputeId: string, signal?: AbortSignal) =>
    request<DisputeResponse>(`/api/v1/disputes/${disputeId}`, disputeResponseSchema, { signal }),

  /** MODERATOR/ADMIN only. */
  listDisputes: (query: DisputesQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/disputes${toQueryString(query)}`,
      paginatedResponseSchema(disputeResponseSchema),
      { signal },
    ),

  submitRebuttal: (disputeId: string, body: RebuttalRequest, idempotencyKey: string) =>
    request<DisputeResponse>(`/api/v1/disputes/${disputeId}/rebuttal`, disputeResponseSchema, {
      method: "POST",
      body: rebuttalRequestSchema.parse(body),
      idempotencyKey,
    }),

  presignDisputeEvidence: (
    orderId: string,
    body: { filename: string; declaredMimeType: string; sizeBytes: number },
    idempotencyKey: string,
  ) =>
    request<PresignUploadResponse>(
      `/api/v1/orders/${orderId}/disputes/evidence/presign`,
      presignUploadResponseSchema,
      { method: "POST", body, idempotencyKey },
    ),

  completeDisputeEvidence: (
    orderId: string,
    uploadId: string,
    etag: string | undefined,
    idempotencyKey: string,
  ) =>
    request<UploadResponse>(
      `/api/v1/orders/${orderId}/disputes/evidence/${uploadId}/complete`,
      uploadResponseSchema,
      { method: "POST", body: completeUploadRequestSchema.parse({ etag }), idempotencyKey },
    ),

  // --- Seller reviews -----------------------------------------------------

  createReview: (orderId: string, body: CreateReviewRequest, idempotencyKey: string) =>
    request<SellerReviewResponse>(`/api/v1/orders/${orderId}/review`, sellerReviewResponseSchema, {
      method: "POST",
      body: createReviewRequestSchema.parse(body),
      idempotencyKey,
    }),

  getSellerRating: (sellerId: string, signal?: AbortSignal) =>
    request<SellerRatingSummary>(`/api/v1/sellers/${sellerId}/rating`, sellerRatingSummarySchema, {
      auth: false,
      signal,
    }),

  getSellerReviews: (sellerId: string, query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/sellers/${sellerId}/reviews${toQueryString(query)}`,
      paginatedResponseSchema(sellerReviewResponseSchema),
      { auth: false, signal },
    ),

  // --- Chat -----------------------------------------------------------------

  startConversation: (listingId: string) =>
    request<ConversationResponse>(
      `/api/v1/listings/${listingId}/conversations`,
      conversationResponseSchema,
      { method: "POST" },
    ),

  getConversations: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(
      `/api/v1/conversations${toQueryString(query)}`,
      paginatedResponseSchema(conversationResponseSchema),
      { signal },
    ),

  getMessages: (conversationId: string, signal?: AbortSignal) =>
    request(`/api/v1/conversations/${conversationId}/messages`, messagesListResponseSchema, {
      signal,
    }),

  sendMessage: (conversationId: string, body: CreateMessageRequest) =>
    request<MessageResponse>(
      `/api/v1/conversations/${conversationId}/messages`,
      messageResponseSchema,
      { method: "POST", body: createMessageRequestSchema.parse(body) },
    ),

  // --- Contact -----------------------------------------------------------------

  /** Public, unauthenticated — relies on `turnstileBridge`'s token, same as new-account
   *  creation. Caller is responsible for mounting `<TurnstileWidget>` on the page first. */
  sendContactMessage: (body: ContactRequest) =>
    request(`/api/v1/contact`, contactResponseSchema, {
      method: "POST",
      auth: false,
      body: contactRequestSchema.parse(body),
    }),

  // --- Support tickets (separate from disputes) --------------------------------

  createTicket: (body: CreateTicketRequest) =>
    request<TicketDetailResponse>("/api/v1/tickets", ticketDetailResponseSchema, {
      method: "POST",
      body: createTicketRequestSchema.parse(body),
    }),

  getTickets: (query: ListingsQuery = {}, signal?: AbortSignal) =>
    request(`/api/v1/tickets${toQueryString(query)}`, paginatedResponseSchema(ticketResponseSchema), {
      signal,
    }),

  getTicket: (ticketId: string, signal?: AbortSignal) =>
    request<TicketDetailResponse>(`/api/v1/tickets/${ticketId}`, ticketDetailResponseSchema, {
      signal,
    }),

  addTicketMessage: (ticketId: string, body: CreateTicketMessageRequest) =>
    request<TicketDetailResponse>(
      `/api/v1/tickets/${ticketId}/messages`,
      ticketDetailResponseSchema,
      { method: "POST", body: createTicketMessageRequestSchema.parse(body) },
    ),
};
